import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    AppHeader, AppHeaderOption,
    DataList,
    ListItemElement,
    RoundButton,
    Searchbox,
    Spinner
} from "@tag/tag-components-react-v4";
import {
    ArrowRightFilled, CircleInfoLined,
    Database2Filled,
    DocList2Filled,
    IDLined,
    MenuFilled,
    SearchFilled,
    WeatherCloudLined,
    CheckboxTickedFilled, CheckboxEmptyLined
} from "@tag/tag-icons";
import CustomerService from "../services/CustomerService";
import {CustomerDTO} from "../types/CustomerDTO";
import {Customer} from "../types/Customer";
import CustomerMenuModal from "./CustomerMenuModal";
import styles from './CustomerList.module.scss';
import CustomerInfoModal from "./CustomerInfoModal";
import HelperMethods from "../../General/services/HelperMethods";
import { FilledInput } from '@mui/material';

function CustomerList() {

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedCustomerForMenu, setSelectedCustomerForMenu] = useState<CustomerDTO|undefined>();
    const [selectedCustomerForInfo, setSelectedCustomerForInfo] = useState<CustomerDTO|undefined>();
    const [alert, setAlert] = useState<{type: any, message: string}>();

    useEffect(() => {
        loadCustomerData();
    }, []);

    const loadCustomerData = async () => {
        setLoading(true);
        
        try {
            const customers = await CustomerService.getAllCustomers();
            console.log('API response customers:', customers);
            setAllCustomers(customers);
        } catch (error) {
            console.error('Error loading customer data:', error);
        } finally {
            setLoading(false);
        }
    }

    // Function to update a single customer's Care Rostering status
    const updateCustomerCareRosteringStatus = async (clientId: number, isEnabled: boolean) => {
        try {
            // Find the customer in the array
            const customerIndex = allCustomers.findIndex(c => c.clientId === clientId);
            if (customerIndex === -1) return;
            
            // Create a new array with the updated customer
            const updatedCustomers = [...allCustomers];
            updatedCustomers[customerIndex] = {
                ...updatedCustomers[customerIndex],
                isCareRosteringEnabled: isEnabled
            };
            
            // Update the state
            setAllCustomers(updatedCustomers);
        } catch (error) {
            console.error(`Error updating ACR status for customer ${clientId}:`, error);
        }
    }
    
    const getCustomerData = (): CustomerDTO[] => {
        if (!searchTerm) {
            return [];
        }

        const data = HelperMethods.advancedFuseSearch(searchTerm, allCustomers, ['name', 'clientId', 'databaseName', 'accessOrganisationId']);

        let returnedData: CustomerDTO[] = [];
        data.forEach((n) => {
            returnedData.push(
                {
                    image: undefined,
                    name: n.name,
                    initials: HelperMethods.getInitials(n.name),
                    appgrp: n.appGroup,
                    appgrpServerId: n.appGroupServerId,
                    clientId: n.clientId,
                    databaseName: n.databaseName,
                    serviceName: n.serviceName,
                    isCareRosteringEnabled: n.isCareRosteringEnabled,
                    accessOrganisationId: n.accessOrganisationId,
                    accessWorkspacePrivateApiKey: n.accessWorkspacePrivateApiKey,
                    accessWorkspacePublicApiKey: n.accessWorkspacePublicApiKey
                }
            )
        });
        
        return returnedData;
    }

    const customerData = useMemo(
        () => getCustomerData(), 
        [searchTerm, allCustomers]
    );
    
    const goToPeoplePlannerSite = (dataItem: CustomerDTO) => {
        window.open(`https://${dataItem.serviceName}-${dataItem.appgrp}.peopleplanner.biz/Common/Security/LoginDetail.aspx?LoginType=1&PPEasyLogin=null`, '_blank');
    }

    let content;
    if (loading) {
        content = <Spinner/>
    } else {
        content = (
          <>
            <AppHeader heading="Customer List" icon={<SearchFilled />}>
              <div
                className={"customer-list-search-container"}
                style={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Searchbox
                  onSearch={(e) => setSearchTerm(e)}
                  clearButton
                  style={{ width: "100%" }}
                />
              </div>
            </AppHeader>
            <DataList
              avatarProps={{
                srcField: "image",
                placeholderField: "initials",
                accent: "teal",
              }}
              primaryField="name"
              renderRightElements={(dataItem) => (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <RoundButton
                    icon={<ArrowRightFilled />}
                    accent={"teal"}
                    onClick={() => goToPeoplePlannerSite(dataItem)}
                  />
                  <RoundButton
                    icon={<CircleInfoLined />}
                    accent={"teal"}
                    onClick={() => setSelectedCustomerForInfo(dataItem)}
                  />
                  <RoundButton
                    icon={<MenuFilled />}
                    accent={"teal"}
                    onClick={() => setSelectedCustomerForMenu(dataItem)}
                  />
                </div>
              )}
              renderSubItemElements={(dataItem) => (
                <>
                  <ListItemElement icon={<IDLined accent="teal" />}>
                    {dataItem.clientId}
                  </ListItemElement>
                  <ListItemElement
                    icon={<WeatherCloudLined accent="teal" />}
                    style={{ marginLeft: "2em" }}
                  >
                    {dataItem.appgrp}
                  </ListItemElement>
                  <ListItemElement
                    icon={<Database2Filled accent="teal" />}
                    style={{ marginLeft: "2em" }}
                  >
                    PeoplePlanner-{dataItem.databaseName}
                  </ListItemElement>
                  <ListItemElement
                        icon={dataItem.isCareRosteringEnabled ? <CheckboxTickedFilled accent="teal"/> : <CheckboxEmptyLined accent="teal"/> }
                        style={{marginLeft: "2em"}}
                  >
                      ACR
                  </ListItemElement>
                </>
              )}
              data={customerData}
            />
            {alert ? (
              <Alert
                className={styles.CustomerListAlert}
                closeTransition="fade"
                type={alert.type}
                showDuration={1200}
                onClosed={() => setAlert(undefined)}
              >
                {alert.message}
              </Alert>
            ) : undefined}
          </>
        );
    }

    return (
        <>
            {content}
            {selectedCustomerForMenu ?
                <CustomerMenuModal
                    selectedCustomer={selectedCustomerForMenu}
                    handleClose={() => {
                        setSelectedCustomerForMenu(undefined)
                    }}
                    setAlert={setAlert}
                    reloadCustomerData={loadCustomerData}
                    updateCustomerCareRosteringStatus={updateCustomerCareRosteringStatus}
                /> : undefined
            }
            {selectedCustomerForInfo ?
                <CustomerInfoModal
                    selectedCustomer={selectedCustomerForInfo}
                    handleClose={() => {
                        setSelectedCustomerForInfo(undefined)
                    }}
                /> : undefined
            }
        </>
    )

}

export default CustomerList;
